/**
 * Pro 页 data
 * 结构对应现网 LoEvent Pro 页（顺序）：
 *   Hero → PosterGrid → TwoModes → Taskboard → SocialImage → SocialPost → Pricing → FAQ
 */
import type { PageData } from '@/content/types';
import { faqJsonLd } from '../../src/content/shared';

// ─── FAQ 内容（顺序 + 文案完全照领导 JSON-LD 版本）───
// 同一份数组同时驱动可见 FAQ section + FAQPage JSON-LD —— 永远一致
const faqItems = [
  {
    q: 'ChatGPT + Canva + Trello is enough. Why LoEvent Pro?',
    a: "That combo works — but you're the glue between three tools that don't talk to each other. LoEvent Pro makes you click — strategy generates the plan, the plan generates the tasks, the tasks know your industry. Type. Drag. Fill. Or just click.",
  },
  {
    q: 'How is LoEvent Pro different from Cvent or Bizzabo?',
    a: 'Different category. Cvent and Bizzabo are execution tools — registration, check-in, analytics. LoEvent Pro is pre-event — strategy, posters, task board, social content. Use LoEvent Pro to plan, Luma or Eventbrite to publish.',
  },
  {
    q: '65% of planners use AI but only 16% see results. Why would LoEvent be different?',
    a: "The gap isn't AI capability — it's workflow structure. Generic AI requires you to know what to ask. LoEvent Pro's click-to-decide system gives you the right questions in the right order, built from professional event planning logic. No prompting. No blank page. Guided decisions from strategy to published assets. (Source: Cvent/Northstar Pulse Survey, 2026)",
  },
  {
    q: 'What does click-to-decide actually mean?',
    a: 'Every decision point — event type, audience tier, vibe, content strategy — is a structured choice you click through. The AI generates based on what you select, not what you describe.',
  },
  {
    q: 'Our team has no dedicated event planner. Is this built for us?',
    a: 'Yes. 45% of event teams are 1-3 people (Bizzabo, 2026). LoEvent is designed for exactly that team — professional outputs without specialist headcount. Industry knowledge is embedded in the product, not assumed from the user.',
  },
  {
    q: 'How is LoEvent different from AI tools that also generate event posters?',
    a: "Generic AI tools produce acceptable visuals with generic aesthetics. LoEvent's poster styles are purpose-built for AI conferences, developer communities, and Web3 events. The differentiation isn't resolution. It's relevance.",
  },
  {
    q: 'Can it handle multi-venue or multi-track events?',
    a: 'Currently optimized for focused events: 1-2 days, single venue. Multi-track support is on the roadmap.',
  },
];

// ─── SoftwareApplication schema（按领导给的原文） ───
const softwareAppJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'LoEvent Pro',
  url: 'https://www.loevent.com/pro',
  operatingSystem: 'Web',
  applicationCategory: 'BusinessApplication',
  description:
    'AI event planning workflow for 1-3 person marketing teams at AI tech companies. Click-to-decide strategy planner, 40+ industry-native poster styles, auto-generated task board, and social content — in one session.',
  offers: {
    '@type': 'Offer',
    price: '0.00',
    priceCurrency: 'USD',
    description: 'Free trial. Single event $50. Annual $250.',
  },
  featureList: [
    '40+ Industry-Native AI Poster Styles',
    'Strategy-First Pre-Event Planner',
    'AI Task Board Auto-Generated from Event Plan',
    'Social Content for LinkedIn, X, and RED NOTE',
    'AI Tech and Web3 Industry Intelligence Built In',
  ],
  audience: {
    '@type': 'BusinessAudience',
    audienceType: '1-3 person marketing teams at AI tech companies, Seed to Series B',
  },
};

export const data: PageData = {
  seo: {
    title: 'LoEvent Pro — AI Workflow Agent for Tech Marketing Teams',
    description:
      'The AI event planner that delivers event poster, strategy and social post generation that creates growth. Built specifically for AI/TECH/WEB 3 industry in-person events.',
    ogImage: '/images/ogimage.jpg',
    jsonLd: [softwareAppJsonLd, faqJsonLd(faqItems)],
  },
  sections: [
    {
      type: 'hero',
      data: {
        pill: { label: '1.0 Beta Online', dot: true },
        headline: 'Click To Plan, Click For Growth',
        subheadline:
          'The AI event planner that delivers event poster and strategy that creates growth. Built specifically for AI/TECH/WEB3 industry in-person events.',
        cta: { label: 'Start Planning Free', href: 'https://app.loevent.com/pro' },
        screenshot: {
          src: '/images/pro-hero.webp',
          alt: 'LoEvent Pro dashboard showing AI-generated event poster, strategy panel, and task board for an AI developer meetup',
          width: 1440,
          height: 812,
        },
      },
    },
    {
      type: 'poster-grid',
      data: {
        eyebrow: 'POSTER DESIGN',
        title: 'Select From 40+ Built-In Industry-Specific Style Posters Based On Your Event Type.',
        body: 'AI intelligently places event details, logos, and key information with professional layout and typography.',
        cta: { label: 'Get Started', href: 'https://app.loevent.com/pro' },
        image: {
          src: '/images/posters-grid.webp',
          alt: 'LoEvent Pro AI-generated posters in 40+ art styles for AI conferences, developer meetups, and Web3 events, optimized for social media and print',
          width: 1600,
          height: 640,
        },
      },
    },
    {
      type: 'two-modes',
      data: {
        eyebrow: 'OUR SERVICES',
        title: 'Two Modes. Same Toolkit.',
        body: "Most marketing teams don't start from zero — they start from somewhere. Some have a brief and need a plan. Some already have a plan and need the assets. LoEvent Pro fits both: pick the mode that matches what you have in hand, and the toolkit delivers the same professional output either way.",
        cta: { label: 'Get Started', href: 'https://app.loevent.com/pro' },
        modes: [
          {
            title: 'Tool Mode',
            desc: 'Already have a plan? Paste it in. LoEvent Pro auto-extracts event details, host info, and agenda — every tool unlocks instantly.',
          },
          {
            title: 'Workflow Mode',
            desc: 'Walk through Event Details → Pre-Event Planner → Event Planner. Finish the strategy first, and the full toolkit opens up at the end.',
          },
        ],
      },
    },
    {
      type: 'taskboard',
      data: {
        eyebrow: 'EVENT TASKBOARD',
        title: 'From PLAN To Exact Task, In 1 Click',
        body: "When your Event Planner is done, LoEvent Pro extracts every task from it — speaker outreach, vendor deadlines, venue logistics, day-of operations. Nothing is typed from scratch. Nothing is missed. Your entire pre-event workload is organized, prioritized, and scheduled before you've opened a single spreadsheet.",
        bullets: [
          'Auto-generated from your Event Planner — no blank boards, no manual transfer.',
          'Four tracks: Marketing, Business, Venue, and General — priorities and dependencies pre-mapped.',
          "Daily, Calendar, and Task views — see what's due today or the full timeline at a glance.",
          'Drag to reschedule — adjust dates and duration directly on the calendar.',
          'Zero gaps before event day.',
        ],
        cta: { label: 'Get Started', href: 'https://app.loevent.com/pro' },
        image: {
          src: '/images/taskboard.webp',
          alt: 'LoEvent Pro AI Task Board with four-dimension task structure auto-generated from event plan, built for AI tech event teams',
          width: 1440,
          height: 810,
        },
      },
    },
    /* TEMP HIDDEN — restore when copy/asset ready (2026-05-12)
    {
      type: 'social-image',
      data: {
        eyebrow: 'SOCIAL IMAGE',
        title: 'Bridging The Digital Skill Divide',
        body: 'We believe in the power of personal stories, and here, we celebrate voices that often go unheard.',
        cta: { label: 'Join Our Team', href: 'https://app.loevent.com/pro' },
        image: {
          src: '/images/product.webp',
          alt: 'LoEvent Pro cross-platform social content preview showing pre-event posts for AI conference, formatted for LinkedIn, X, and RED NOTE',
          width: 1200,
          height: 800,
        },
        imagePosition: 'right',
      },
    },
    */
    {
      type: 'social-post',
      data: {
        eyebrow: 'SOCIAL POST',
        title: 'Tone, Focus, Length — Click. Post Done.',
        body: 'No copy briefs. No prompt writing. No reformatting across platforms. Select three things and LoEvent generates platform-native social content grounded in your actual event data — ready to publish across Community, Twitter/X, and RED NOTE.',
        bullets: [
          { label: 'Tone', desc: 'Formal, Casual, Explanatory, or Playful. One click, consistent voice across every post.' },
          { label: 'Focus', desc: 'Basic Information, Event Agenda, Guest Profile, or Event Purpose. One angle per session, one clear message per post.' },
          { label: 'Length', desc: 'Short, Medium, or Long. Calibrated per platform, no manual trimming.' },
        ],
        cta: { label: 'Get Started', href: 'https://app.loevent.com/pro' },
        image: {
          src: '/images/social-post.webp',
          alt: 'LoEvent Pro social post generator with tone, focus, and length controls for AI developer community engagement',
          width: 1440,
          height: 810,
        },
      },
    },
    {
      type: 'pricing',
      data: {
        title: '8 Events for $250 — or $400 if you buy them one by one',
        subtitle: 'Beta pricing ends at 1.0 release',
        tiers: [
          {
            name: 'Free Trial',
            price: '$0',
            features: [
              '2 AI proposals per category — strategy, content, venue, tasks',
              '10 posters (480p download) · 10 social posts per platform',
              'AI Task Board · Budget · Industry trends',
            ],
            cta: { label: 'Sign Up', href: 'https://app.loevent.com/pro' },
            footnote: 'Sponsorship & marketing proposals not included',
          },
          {
            name: '1 Event Pack',
            price: '$50',
            featured: true,
            features: [
              '10 AI proposals — strategy, content, venue, partners, marketing',
              '10 posters (1K generate + download) + Text Extract',
              '8 guest profiles · 5 target attendee profiles',
              '20 social posts per platform · Task Board · Budget',
            ],
            cta: { label: 'Sign Up', href: 'https://app.loevent.com/pro' },
          },
          {
            name: 'Annual Package',
            price: '$250',
            features: [
              '80 AI proposals per category across 8 events',
              '80 posters (4K generate + download) · 8 hosts',
              '64 guest profiles · 40 target attendee profiles',
              '160 social posts per platform · Full Task Board · Budget',
            ],
            cta: { label: 'Sign Up', href: 'https://app.loevent.com/pro' },
          },
          {
            name: 'Enterprise',
            price: 'Custom',
            features: [
              'Custom feature development — request features built for your workflow (e.g., social image generation, branded poster series, bespoke integrations)',
              'Custom sponsorship & co-marketing collaboration',
              'Custom quota across standard features — proposals, posters, profiles, social, task board, budget',
              'Multi-host workspace · team seats · dedicated onboarding',
            ],
            cta: { label: 'Contact Sales', href: 'mailto:sales@loevent.com' },
            footnote: 'For brands and agencies that need capabilities beyond the standard product.',
          },
        ],
      },
    },
    {
      type: 'faq',
      data: {
        items: faqItems,
      },
    },
  ],
};
